import React, { useState } from "react";
import { Check, Briefcase, Search, Trophy, User, ArrowRight, Clock, Users } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingContact from "../components/FloatingContact";
import ApplicationDialog from "../components/ApplicationDialog";
import { useLang } from "../i18n/LangContext";

const NAVY = "#1B2A4A";
const GOLD = "#C9A96E";
const CREAM = "#FAF8F5";

const I18N = {
  uk: {
    kicker: "CAREER TRACK · STUDYGRI",
    h1: "Від напряму до підписаного офферу",
    sub: "Три поетапні програми для студентів, які хочуть свідомо вийти на ринок праці. Наставники, що самі нещодавно проходили стажування в Google, Apple, Microsoft.",
    cta: "Записатися на консультацію",
    stagesTitle: "3 етапи — обирайте за поточною стадією навчання",
    programsIntro: "Кожна програма веде студента поетапно: напрям → стажування → оффер.",
    keyBenefitsTitle: "Чому Career Track працює",
    keyBenefits: [
      { t: "Наставник близький за віком", d: "Той, хто сам нещодавно проходив стажування, співбесіди й переговори про оффер." },
      { t: "Шлях, а не разова консультація", d: "Кожна програма веде студента поетапно, готує до наступної." },
      { t: "Фокус на результат", d: "Не абстрактне «зростання», а конкретні завдання: CV, LinkedIn, outreach, співбесіди, переговори." },
      { t: "Закритий набір", d: "Обмежена кількість місць на кожен набір: більше уваги та живого зворотного зв'язку." }
    ],
    disclaimer: "Програми готують до пошуку, але не гарантують саме стажування чи оффер — результат залежить від ринку, компаній і зусиль студента.",
    extraTitle: "Персональна сесія 1-1",
    extraDesc: "Точкова робота один на один поза курсом (60 хв): розбір CV, стратегії чи підготовка до конкретної співбесіди.",
    extraPrice: "€500 / 60 хв",
    programs: [
      {
        num: "01", stage: "До університету / 1 курс",
        name: "Foundation", price: "€500", format: "3 сесії × 90 хв", intake: "Один набір на рік · серпень – листопад",
        icon: Search,
        desc: "Починаємо вибудовувати напрям та особистий бренд, щоб не втратити перші роки й не зробити типових помилок старту.",
        result: "Обрати напрям і не втратити перші роки.",
        blocks: [
          "Карта індустрій і напрямів — огляд сфер, розбір сильних сторін, первинний вектор",
          "Corporate vs Startups — плюси й мінуси корпорації та стартапу",
          "Особистий бренд і LinkedIn з нуля",
          "University → Work: як пов'язати вибір університету з майбутньою кар'єрою"
        ]
      },
      {
        num: "02", stage: "1-2 курс · Стажування",
        name: "Internship Guide", price: "€900", format: "5 сесій × 90 хв", intake: "Два набори: вересень – листопад та лютий – квітень",
        icon: Briefcase, highlight: true,
        desc: "Як отримати перше стажування: зібрати CV, вибудувати бренд і навчитися заходити в компанії напряму. Працюємо на реальних кейсах.",
        result: "Вийти на пошук стажування підготовленим.",
        blocks: [
          "Ландшафт стажувань і цільовий список — карта, тайминг, ролі",
          "CV та Cover Letter — структура, приклади, чек-лист",
          "Побудова бренду в LinkedIn — щоб знаходили рекрутери",
          "Cold & Warm Outreach — реальне виконання під час сесії",
          "Стратегія подачі та перша співбесіда"
        ]
      },
      {
        num: "03", stage: "Bachelor / Master / Робота",
        name: "Job Offer Guide", price: "€1 400", format: "6 сесій × 90 хв + консультація 1-1", intake: "Два набори: вересень – листопад та лютий – квітень, 1 сесія на місяць",
        icon: Trophy,
        desc: "Повний цикл: від стратегії подачі до переговорів про зарплату. Темп розтягнутий на кілька місяців — кожна сесія підлаштовується під реальний етап пошуку студента.",
        result: "Вийти на пошук роботи й вести переговори про оффер.",
        blocks: [
          "Стратегія подачі заявок — таргетинг, тайминг, воронка",
          "CV під роль / шаблони — сумісність з ATS",
          "Бренд у LinkedIn для пошуку роботи — нетворкінг з рекрутерами",
          "Salary benchmarking — знати свою вартість до переговорів",
          "Підготовка до співбесіди — behavioral / technical / case",
          "Переговори про оффер + особиста консультація 1-1"
        ]
      }
    ]
  },
  ru: {
    kicker: "CAREER TRACK · STUDYGRI",
    h1: "От направления до подписанного оффера",
    sub: "Три поэтапные программы для студентов, которые хотят осознанно выйти на рынок труда. Наставники, недавно прошедшие стажировки в Google, Apple, Microsoft.",
    cta: "Записаться на консультацию",
    stagesTitle: "3 этапа — выбирайте по текущей стадии обучения",
    programsIntro: "Каждая программа ведёт студента поэтапно: направление → стажировка → оффер.",
    keyBenefitsTitle: "Почему Career Track работает",
    keyBenefits: [
      { t: "Наставник близкий по возрасту", d: "Тот, кто сам недавно проходил стажировки, собеседования и переговоры об оффере." },
      { t: "Путь, а не разовая консультация", d: "Каждая программа ведёт студента поэтапно, готовит к следующей." },
      { t: "Фокус на результат", d: "Конкретные задачи: CV, LinkedIn, outreach, собеседования, переговоры." },
      { t: "Закрытый набор", d: "Ограниченное количество мест — больше внимания и живой обратной связи." }
    ],
    disclaimer: "Программы готовят к поиску, но не гарантируют стажировку или оффер — результат зависит от рынка и усилий студента.",
    extraTitle: "Персональная сессия 1-1",
    extraDesc: "Точечная работа один на один вне курса (60 мин): разбор CV, стратегии или подготовка к конкретному собеседованию.",
    extraPrice: "€500 / 60 мин",
    programs: [
      { num: "01", stage: "До университета / 1 курс", name: "Foundation", price: "€500", format: "3 сессии × 90 мин", intake: "Один набор в год · август – ноябрь", icon: Search,
        desc: "Начинаем выстраивать направление и личный бренд, чтобы не потерять первые годы и не сделать типичных ошибок.",
        result: "Выбрать направление и не потерять первые годы.",
        blocks: ["Карта индустрий и направлений — обзор сфер, сильные стороны","Corporate vs Startups — плюсы и минусы","Личный бренд и LinkedIn с нуля","University → Work: как связать выбор университета с карьерой"] },
      { num: "02", stage: "1-2 курс · Стажировка", name: "Internship Guide", price: "€900", format: "5 сессий × 90 мин", intake: "Два набора: сентябрь – ноябрь и февраль – апрель", icon: Briefcase, highlight: true,
        desc: "Как получить первое стажирование: собрать CV, выстроить бренд и научиться заходить в компании напрямую.",
        result: "Выйти на поиск стажировки подготовленным.",
        blocks: ["Ландшафт стажировок и целевой список","CV и Cover Letter — структура, примеры, чек-лист","Построение бренда в LinkedIn","Cold & Warm Outreach — реальное выполнение","Стратегия подачи и первое собеседование"] },
      { num: "03", stage: "Bachelor / Master / Работа", name: "Job Offer Guide", price: "€1 400", format: "6 сессий × 90 мин + 1-1", intake: "Два набора в год, 1 сессия в месяц", icon: Trophy,
        desc: "Полный цикл: от стратегии подачи до переговоров о зарплате. Каждая сессия подстраивается под реальный этап поиска.",
        result: "Выйти на поиск работы и вести переговоры об оффере.",
        blocks: ["Стратегия подачи заявок","CV под роль / шаблоны — ATS","Бренд в LinkedIn для работы","Salary benchmarking","Подготовка к собеседованию","Переговоры об оффере + 1-1 консультация"] }
    ]
  },
  en: {
    kicker: "CAREER TRACK · STUDYGRI",
    h1: "From direction to a signed offer",
    sub: "Three step-by-step programs for students who want to enter the job market consciously. Mentors who recently went through internships at Google, Apple, Microsoft.",
    cta: "Book a consultation",
    stagesTitle: "3 stages — choose by your current stage of studies",
    programsIntro: "Each program guides the student step-by-step: direction → internship → offer.",
    keyBenefitsTitle: "Why Career Track works",
    keyBenefits: [
      { t: "Mentor close in age", d: "Someone who recently went through internships, interviews and offer negotiations." },
      { t: "A path, not a one-off", d: "Each program guides the student step-by-step, preparing for the next." },
      { t: "Focus on results", d: "Concrete tasks: CV, LinkedIn, outreach, interviews, negotiations." },
      { t: "Limited enrollment", d: "Limited seats per cohort — more attention and live feedback." }
    ],
    disclaimer: "Programs prepare for the search but do not guarantee an internship or offer — results depend on the market and the student's efforts.",
    extraTitle: "Personal 1-1 session",
    extraDesc: "Targeted 1-on-1 work outside the course (60 min): CV review, strategy or interview preparation.",
    extraPrice: "€500 / 60 min",
    programs: [
      { num: "01", stage: "Pre-university / Year 1", name: "Foundation", price: "€500", format: "3 sessions × 90 min", intake: "One cohort per year · August – November", icon: Search,
        desc: "Start building your direction and personal brand to avoid losing the first years and typical starter mistakes.",
        result: "Choose a direction and don't waste your first years.",
        blocks: ["Map of industries and directions — overview, strengths","Corporate vs Startups — pros and cons","Personal brand and LinkedIn from scratch","University → Work: link university choice to career"] },
      { num: "02", stage: "Year 1-2 · Internship", name: "Internship Guide", price: "€900", format: "5 sessions × 90 min", intake: "Two cohorts: Sep – Nov and Feb – Apr", icon: Briefcase, highlight: true,
        desc: "How to land your first internship: build CV, personal brand, and learn to approach companies directly. Real cases during the course.",
        result: "Be ready for the internship search.",
        blocks: ["Internship landscape and target list","CV and Cover Letter — structure, examples","LinkedIn brand building — get found by recruiters","Cold & Warm Outreach — real execution","Application strategy and first interview"] },
      { num: "03", stage: "Bachelor / Master / Job", name: "Job Offer Guide", price: "€1,400", format: "6 sessions × 90 min + 1-1", intake: "Two cohorts per year, 1 session per month", icon: Trophy,
        desc: "Full cycle: from application strategy to salary negotiation. Sessions adapt to the student's real search stage.",
        result: "Enter the job market and negotiate your offer.",
        blocks: ["Application strategy","CV per role / templates — ATS","LinkedIn brand for job search","Salary benchmarking","Interview preparation","Offer negotiation + 1-1"] }
    ]
  }
};

export default function CareerTrackPage() {
  const { lang } = useLang();
  const t = I18N[lang] || I18N.en;
  const [formOpen, setFormOpen] = useState(false);

  return (
    <div className="min-h-screen" style={{ background: CREAM }}>
      <Header onOpenForm={() => setFormOpen(true)} />
      <main className="pt-28 md:pt-36 pb-20" data-testid="career-track-page">
        {/* HERO */}
        <section className="relative overflow-hidden pb-14 md:pb-20 px-5 md:px-8" style={{ background: NAVY, color: '#fff' }}>
          <div className="max-w-5xl mx-auto text-center pt-14 md:pt-20">
            <div className="inline-block text-[11px] font-bold tracking-[0.28em] uppercase mb-5 px-4 py-1.5 rounded-full" style={{ background: GOLD, color: NAVY }}>{t.kicker}</div>
            <h1 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.1] text-balance" style={{ fontWeight: 900, letterSpacing: '-0.025em' }}>{t.h1}</h1>
            <p className="mt-6 text-base md:text-xl max-w-3xl mx-auto" style={{ color: 'rgba(255,255,255,0.85)' }}>{t.sub}</p>
            <button onClick={() => setFormOpen(true)} data-testid="ct-hero-cta" className="mt-9 inline-flex items-center gap-2 px-8 h-13 py-3.5 rounded-full font-bold text-base transition hover:opacity-90" style={{ background: GOLD, color: NAVY }}>
              {t.cta} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>

        {/* PROGRAMS */}
        <section className="max-w-6xl mx-auto px-5 md:px-8 -mt-10 md:-mt-14">
          <div className="text-center mb-10">
            <h2 className="font-display text-2xl md:text-3xl" style={{ color: NAVY, fontWeight: 900 }}>{t.stagesTitle}</h2>
            <p className="text-sm md:text-base mt-2" style={{ color: '#666' }}>{t.programsIntro}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {t.programs.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="relative rounded-3xl p-7 md:p-8 flex flex-col" style={{
                  background: p.highlight ? NAVY : '#fff',
                  color: p.highlight ? '#fff' : NAVY,
                  border: `2px solid ${p.highlight ? GOLD : '#E8E0D5'}`,
                  boxShadow: p.highlight ? '0 30px 80px -25px rgba(27,42,74,0.35)' : '0 12px 40px -20px rgba(27,42,74,0.15)'
                }} data-testid={`ct-program-${i}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: p.highlight ? GOLD : NAVY }}>
                      <Icon className="w-5 h-5" style={{ color: p.highlight ? NAVY : GOLD }} />
                    </div>
                    <div className="font-mono text-xs opacity-70">{p.num}</div>
                  </div>
                  <div className="text-xs uppercase tracking-widest mb-2" style={{ color: GOLD }}>{p.stage}</div>
                  <div className="font-display text-2xl md:text-3xl mb-2" style={{ fontWeight: 900 }}>{p.name}</div>
                  <div className="text-sm mb-4" style={{ color: p.highlight ? 'rgba(255,255,255,0.75)' : '#666' }}>{p.desc}</div>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="font-display text-3xl md:text-4xl" style={{ fontWeight: 900 }}>{p.price}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs mb-2" style={{ color: p.highlight ? 'rgba(255,255,255,0.75)' : '#666' }}>
                    <Clock className="w-3.5 h-3.5" /> {p.format}
                  </div>
                  <div className="flex items-start gap-3 text-xs mb-5" style={{ color: p.highlight ? 'rgba(255,255,255,0.75)' : '#666' }}>
                    <Users className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {p.intake}
                  </div>
                  <ul className="space-y-2 text-sm mb-6 flex-1">
                    {p.blocks.map((b, j) => (
                      <li key={j} className="flex gap-2">
                        <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: GOLD }} />
                        <span style={{ color: p.highlight ? 'rgba(255,255,255,0.9)' : '#2C2C2C' }}>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="text-xs font-semibold mb-4 p-3 rounded-lg" style={{ background: p.highlight ? 'rgba(201,169,110,0.15)' : 'rgba(201,169,110,0.10)', color: p.highlight ? GOLD : NAVY }}>
                    ✦ {p.result}
                  </div>
                  <button onClick={() => setFormOpen(true)} data-testid={`ct-cta-${i}`} className="w-full h-12 rounded-full font-bold text-sm transition hover:opacity-90" style={{ background: p.highlight ? GOLD : NAVY, color: p.highlight ? NAVY : '#fff' }}>
                    {t.cta}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        {/* 1-1 SESSION */}
        <section className="max-w-6xl mx-auto px-5 md:px-8 mt-12">
          <div className="rounded-3xl p-7 md:p-10 flex flex-col md:flex-row items-center gap-6 md:gap-10" style={{ background: '#fff', border: `1px solid #E8E0D5` }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center shrink-0" style={{ background: NAVY }}>
              <User className="w-7 h-7" style={{ color: GOLD }} />
            </div>
            <div className="flex-1 text-center md:text-left">
              <div className="text-xs uppercase tracking-widest mb-2" style={{ color: GOLD }}>{t.extraTitle}</div>
              <div className="text-sm md:text-base" style={{ color: '#2C2C2C' }}>{t.extraDesc}</div>
            </div>
            <div className="text-center md:text-right">
              <div className="font-display text-2xl md:text-3xl mb-3" style={{ color: NAVY, fontWeight: 900 }}>{t.extraPrice}</div>
              <button onClick={() => setFormOpen(true)} data-testid="ct-11-cta" className="px-6 h-11 rounded-full font-bold text-sm" style={{ background: NAVY, color: '#fff' }}>
                {t.cta}
              </button>
            </div>
          </div>
        </section>

        {/* KEY BENEFITS */}
        <section className="max-w-6xl mx-auto px-5 md:px-8 mt-16">
          <h2 className="font-display text-2xl md:text-3xl text-center mb-8" style={{ color: NAVY, fontWeight: 900 }}>{t.keyBenefitsTitle}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {t.keyBenefits.map((k, i) => (
              <div key={i} className="rounded-2xl p-6" style={{ background: '#fff', border: '1px solid #E8E0D5' }}>
                <div className="font-display text-lg mb-2" style={{ color: NAVY, fontWeight: 900 }}>{k.t}</div>
                <div className="text-sm" style={{ color: '#666' }}>{k.d}</div>
              </div>
            ))}
          </div>
          <p className="text-center text-xs mt-8 max-w-2xl mx-auto" style={{ color: '#888' }}>{t.disclaimer}</p>
        </section>
      </main>
      <Footer />
      <FloatingContact onOpenForm={() => setFormOpen(true)} />
      <ApplicationDialog open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
}
